import { Injectable } from '@angular/core';
import { forkJoin, of, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatChartsApiService } from './chat-charts-api.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { ChartData, RawScorePoint } from './chart-data.models';
import { ScoreType } from 'src/app/shared/models/score-time-series.interface';

@Injectable({
  providedIn: 'root',
})
export class ChartDataService {
  constructor(private apiService: ChatChartsApiService) {}

  fetchTrendingChartData(
    timeperiod: string,
    scoreType: string,
    companyName: string,
  ): Observable<ChartData> {
    const timeframeLength = DateUtils.chartFromTo(timeperiod).period;
    const shortPeriod = timeframeLength <= 7 ? 1 : 7;
    const longPeriod = timeframeLength <= 7 ? 7 : 30;

    const isEsg = scoreType === 'esg';
    const isSentiment = scoreType === 'sentiment';

    return forkJoin({
      sentimentScoreShort: isSentiment
        ? this.apiService.fetchSentimentTimeSeries(
            companyName,
            timeperiod,
            shortPeriod,
          )
        : of({ score_ts: [] }),

      sentimentScoreLong: isSentiment
        ? this.apiService.fetchSentimentTimeSeries(
            companyName,
            timeperiod,
            longPeriod,
          )
        : of({ score_ts: [] }),

      scoreE: isEsg
        ? this.apiService.fetchScoreTimeSeries(
            'e_standard',
            companyName,
            timeperiod,
          )
        : of({ score_ts: [] }),

      scoreS: isEsg
        ? this.apiService.fetchScoreTimeSeries(
            's_standard',
            companyName,
            timeperiod,
          )
        : of({ score_ts: [] }),

      scoreG: isEsg
        ? this.apiService.fetchScoreTimeSeries(
            'g_standard',
            companyName,
            timeperiod,
          )
        : of({ score_ts: [] }),

      scores: isSentiment
        ? of({ score_ts: [] })
        : this.apiService.fetchScoreTimeSeries(
            scoreType as ScoreType,
            companyName,
            timeperiod,
          ),
    }).pipe(
      map((resArgs) => ({
        scoreE: this.toScorePairs(resArgs.scoreE.score_ts),
        scoreS: this.toScorePairs(resArgs.scoreS.score_ts),
        scoreG: this.toScorePairs(resArgs.scoreG.score_ts),
        score: this.toScorePairs(resArgs.scores.score_ts),
        sentimentScoreShort: this.toSentimentPairs(
          resArgs.sentimentScoreShort.score_ts,
        ),
        sentimentScoreLong: this.toSentimentPairs(
          resArgs.sentimentScoreLong.score_ts,
        ),
      })),
    );
  }

  private toScorePairs(data: RawScorePoint[]): [number, number][] {
    return data
      .filter((item) => item.score !== null)
      .map((item) => [
        new Date(item.date).getTime(),
        parseFloat(item.score.toFixed(2)),
      ]);
  }

  private toSentimentPairs(data: RawScorePoint[]): [number, number][] {
    return data
      .filter((item) => item.score !== null)
      .map((item) => [
        new Date(item.date).getTime(),
        Math.round(item.score * 100),
      ]);
  }
}
